import React, { useState, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useContractor } from 'context/ContractorContext';
import { getContractorProfile, getCompanyPayrolls, previewPayroll } from 'services/contractorPortal';
import axiosInstance from '../../axios/axiosInstance';
import ENV from 'constants/env';

const companyId = () => localStorage.getItem('companyId') || ENV.COMPANY_ID;

async function downloadContractorPaystubPdf(contractorId, payrollId, fileName) {
    try {
        const res = await axiosInstance.get(
            `/companies/${companyId()}/contractors/${contractorId}/paystubs/${payrollId}/download`,
            { responseType: 'blob' }
        );
        const contentType = res.headers['content-type'] || 'application/pdf';
        const isPdf = contentType.includes('pdf');
        const blob = new Blob([res.data], { type: contentType });
        const url  = URL.createObjectURL(blob);
        const dlName = fileName || `paystub-${payrollId}.pdf`;
        if (isPdf) {
            const a = document.createElement('a');
            a.href = url; a.download = dlName;
            document.body.appendChild(a); a.click(); a.remove();
        } else {
            const tab = window.open(url, '_blank');
            if (!tab) {
                const a = document.createElement('a');
                a.href = url; a.download = dlName;
                document.body.appendChild(a); a.click(); a.remove();
            }
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return true;
    } catch { return false; }
}

const fmt     = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '—';

const payRateUnit = (payType) => {
    switch ((payType || '').toLowerCase()) {
        case 'hourly':      return '/ hr';
        case 'weekly':      return '/ wk';
        case 'biweekly':    return '/ 2 wks';
        case 'semimonthly': return '/ semi-mo';
        case 'monthly':     return '/ mo';
        case 'annually':
        case 'salary':      return '/ yr';
        case 'flat':        return 'flat';
        default:            return '';
    }
};

const PayStubModal = ({ payment, contractor, contractorId, ytdData, onClose }) => {
    const [downloading, setDownloading]     = useState(false);
    const [downloadFailed, setDownloadFailed] = useState(false);

    const handleDownload = async () => {
        setDownloading(true); setDownloadFailed(false);
        const name = contractor?.businessName ||
            [contractor?.firstName, contractor?.lastName].filter(Boolean).join('-') || 'contractor';
        const date = payment.payroll.payDate ? payment.payroll.payDate.slice(0, 10) : payment.payroll.id;
        const ok = await downloadContractorPaystubPdf(contractorId, payment.payroll.id, `${name}-${date}.pdf`);
        if (!ok) setDownloadFailed(true);
        setDownloading(false);
    };
    const { payroll, cData } = payment;
    const c = contractor || {};
    const displayName = c.businessName || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
    const address = [c.addressLine1, c.city, c.state, c.zip].filter(Boolean).join(', ');
    const grossPay = cData?.grossPay ?? ((cData?.hours ?? 0) * (cData?.payRate ?? 0));
    const pmLabel = (cData?.paymentMethod || '').replace(/([A-Z])/g, ' $1').trim() || '—';

    const handlePrint = () => {
        const el = document.getElementById('cp-stub-printable');
        if (!el) return;
        const w = window.open('', '_blank', 'width=860,height=600');
        w.document.write('<!DOCTYPE html><html><head><title>Payment Statement</title><style>body{font-family:Arial,sans-serif;font-size:13px;color:#222;padding:24px;}table{width:100%;border-collapse:collapse;margin-bottom:12px;}td,th{padding:6px 10px;border-bottom:1px solid #eee;}.text-right{text-align:right;}.fw-bold{font-weight:700;}</style></head><body>');
        w.document.write(el.innerHTML);
        w.document.write('</body></html>');
        w.document.close();
        w.focus();
        w.print();
        w.close();
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 580, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                {/* Header */}
                <div style={{ background: '#1a9fbb', color: '#fff', padding: '18px 24px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 17 }}>Contractor Payment Statement</div>
                        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                            Period: {fmtDate(payroll.startDate)} – {fmtDate(payroll.endDate)} &nbsp;|&nbsp; Pay Date: {fmtDate(payroll.payDate)}
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                </div>

                <div style={{ padding: '24px' }} id="cp-stub-printable">
                    {/* Contractor Info */}
                    <div style={{ background: '#fef9f5', border: '1px solid #f0d0b0', borderRadius: 8, padding: '14px 16px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                        <div>
                            <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Contractor</div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{displayName}</div>
                            {address && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{address}</div>}
                        </div>
                        <div>
                            <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Contractor Type</div>
                            <div style={{ fontWeight: 600 }}>1099 / Independent</div>
                        </div>
                        {cData?.paymentMethod && (
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Payment Method</div>
                                <div style={{ fontWeight: 600 }}>{pmLabel}</div>
                            </div>
                        )}
                    </div>

                    {/* Earnings Table */}
                    <div style={{ fontWeight: 700, fontSize: 13, borderBottom: '2px solid #1a9fbb', paddingBottom: 4, marginBottom: 10 }}>Earnings</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
                        <tbody>
                            {cData?.hours != null && cData.hours > 0 && (
                                <tr style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '7px 0' }}>Hours Worked</td>
                                    <td style={{ padding: '7px 0', textAlign: 'right' }}>{cData.hours} hrs</td>
                                </tr>
                            )}
                            {cData?.payRate != null && (
                                <tr style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '7px 0' }}>Pay Rate</td>
                                    <td style={{ padding: '7px 0', textAlign: 'right' }}>
                                        {fmt(cData.payRate)}{payRateUnit(cData.payType || c.payType) ? ` ${payRateUnit(cData.payType || c.payType)}` : ''}
                                    </td>
                                </tr>
                            )}
                            {cData?.reimbursement > 0 && (
                                <tr style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '7px 0' }}>Reimbursement</td>
                                    <td style={{ padding: '7px 0', textAlign: 'right' }}>{fmt(cData.reimbursement)}</td>
                                </tr>
                            )}
                            <tr style={{ borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                                <td style={{ padding: '10px 0' }}>Gross Pay</td>
                                <td style={{ padding: '10px 0', textAlign: 'right', color: '#2e7d32', fontSize: 15 }}>{fmt(grossPay)}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* 1099 Notice */}
                    <div style={{ background: '#fff8f0', border: '1px solid #ffe0b2', borderRadius: 6, padding: '10px 14px', fontSize: 12, color: '#888', marginBottom: 16 }}>
                        As a 1099 independent contractor, no federal or state taxes are withheld. You are responsible for self-employment taxes.
                    </div>

                    {/* Net Pay Bar */}
                    <div style={{ background: '#f0faf0', border: '1px solid #c3e6c3', borderRadius: 8, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#555', fontSize: 13 }}>Total Payment (No Withholdings)</span>
                        <span style={{ fontWeight: 700, fontSize: 22, color: '#2e7d32' }}>{fmt(grossPay)}</span>
                    </div>
                </div>

                {/* YTD Summary */}
                {ytdData && (
                    <div style={{ margin: '0 24px 16px', background: '#f7f9fc', border: '1px solid #d0d8e4', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Year-to-Date Summary</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', fontSize: 12 }}>
                            {[
                                { label: 'YTD Gross',   value: ytdData.ytdGross,   color: '#222' },
                                { label: 'YTD Net Pay', value: ytdData.ytdNetPay,  color: '#2e7d32' },
                            ].map(({ label, value, color }) => (
                                <div key={label}>
                                    <div style={{ color: '#888', marginBottom: 1 }}>{label}</div>
                                    <div style={{ fontWeight: 700, color }}>{fmt(value)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div style={{ padding: '12px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                        {downloadFailed && (
                            <span style={{ fontSize: 12, color: '#dc3545' }}>PDF unavailable — use Print instead.</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={onClose} style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontSize: 13 }}>Close</button>
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            style={{ background: '#1a9fbb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: downloading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, opacity: downloading ? 0.7 : 1 }}
                        >
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </button>
                        <button onClick={handlePrint} style={{ background: '#1a9fbb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Print</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContractorPayments = () => {
    const { contractorId } = useContractor();
    const [selectedPayment, setSelectedPayment] = useState(null);

    const { data: contractor } = useQuery({
        queryKey: ['cp-contractor', contractorId],
        queryFn: () => getContractorProfile(contractorId),
        enabled: !!contractorId,
    });

    const { data: allPayrolls = [] } = useQuery({
        queryKey: ['cp-company-payrolls'],
        queryFn: getCompanyPayrolls,
        enabled: !!contractorId,
    });

    const approvedPayrolls = useMemo(() => allPayrolls.filter(p => p.status?.toLowerCase() === 'approved'), [allPayrolls]);

    const previewQueries = useQueries({
        queries: approvedPayrolls.map(p => ({
            queryKey: ['cp-preview', p.id],
            queryFn: () => previewPayroll(p.id),
            enabled: !!contractorId,
            staleTime: 1000 * 60 * 10,
        })),
    });

    const payments = useMemo(() => {
        if (!contractorId) return [];
        const result = [];
        approvedPayrolls.forEach((payroll, i) => {
            const preview = previewQueries[i]?.data;
            const cData = preview?.contractors?.find(c => c.contractorId === contractorId || c.id === contractorId);
            if (cData && (cData.grossPay > 0 || cData.reimbursement > 0)) result.push({ payroll, cData });
        });
        return result.sort((a, b) => new Date(b.payroll.payDate) - new Date(a.payroll.payDate));
    }, [approvedPayrolls, previewQueries, contractorId]);

    const isLoading = previewQueries.some(q => q.isLoading);

    const ytdData = useMemo(() => {
        if (!contractorId || isLoading || !selectedPayment) return null;
        const yr = selectedPayment.payroll.payDate ? new Date(selectedPayment.payroll.payDate).getUTCFullYear() : new Date().getFullYear();
        let ytdGross = 0, ytdNetPay = 0;
        approvedPayrolls.forEach((p, i) => {
            const pYr = p.payDate ? new Date(p.payDate).getUTCFullYear() : null;
            if (pYr !== yr) return;
            const preview = previewQueries[i]?.data;
            const ct = preview?.contractors?.find(c => c.contractorId === contractorId || c.id === contractorId);
            if (!ct) return;
            const g = ct.grossPay ?? 0; const r = ct.reimbursement ?? 0;
            ytdGross   += g + r;
            ytdNetPay  += g + r;
        });
        return ytdGross > 0 ? { ytdGross, ytdNetPay } : null;
    }, [approvedPayrolls, previewQueries, isLoading, contractorId, selectedPayment]);

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Payments</h2>
            <p style={{ color: '#6c757d', marginBottom: 24 }}>Your complete payment history from all approved payroll runs.</p>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Payday', 'Period', 'Hours', 'Pay Rate', 'Reimbursement', 'Gross Pay', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Loading payments...</td></tr>
                        ) : payments.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{ padding: '32px', textAlign: 'center' }}>
                                    <div style={{ color: '#aaa', fontSize: 15, marginBottom: 6 }}>No payments found</div>
                                    <div style={{ color: '#ccc', fontSize: 13 }}>Payments will appear here once a payroll run including you is approved.</div>
                                </td>
                            </tr>
                        ) : payments.map((p, i) => {
                            const grossPay = p.cData?.grossPay ?? ((p.cData?.hours ?? 0) * (p.cData?.payRate ?? 0));
                            return (
                                <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payroll.payDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payroll.startDate)} – {fmtDate(p.payroll.endDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.cData?.hours != null ? p.cData.hours : '—'}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.cData?.payRate ? `${fmt(p.cData.payRate)}${payRateUnit(p.cData.payType || contractor?.payType) ? ` ${payRateUnit(p.cData.payType || contractor?.payType)}` : ''}` : '—'}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{p.cData?.reimbursement > 0 ? fmt(p.cData.reimbursement) : '—'}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#198754' }}>{fmt(grossPay)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <button
                                            onClick={() => setSelectedPayment(p)}
                                            style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
                                        >
                                            Pay Stub
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
                <div style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#6c757d' }}>
                    Total: {payments.length} payment{payments.length !== 1 ? 's' : ''}
                </div>
            </div>

            {selectedPayment && (
                <PayStubModal
                    payment={selectedPayment}
                    contractor={contractor}
                    contractorId={contractorId}
                    ytdData={ytdData}
                    onClose={() => setSelectedPayment(null)}
                />
            )}
        </div>
    );
};

export default ContractorPayments;
